import { useLayoutEffect } from "react";

import { useUserContext } from "@/contexts/UserContext/index.context";
import { useFindClientBySubQuery } from "@/libs/generated/hooks";
import { UserRole } from "@/types/models/user.type";

import type { DeliveryFormType } from "../types";
import { fillMandataireFormFromClient } from "../utils/mandataire";

/**
 * For CLIENT users, autofill hidden mandataire/representative fields from
 * findClientBySub — same behavior as DeliveryFormV2 ClientMandataire.
 * Without this, step0Schema fails on empty representative* fields with no UI feedback.
 */
export function useClientMandataireAutofill(form: DeliveryFormType) {
  const { userState } = useUserContext();
  const isAdmin = userState?.["custom:role"] === UserRole.ADMIN;
  const sub = userState?.sub ?? "";

  const alreadyFilled =
    Boolean(form.state.values.step0.representativeFirstName) ||
    Boolean(form.state.values.step0.selectedMandataireId);

  const { data: client, isPending } = useFindClientBySubQuery(
    { GetBySubInput: { sub } },
    {
      enabled: !isAdmin && sub.length > 0 && !alreadyFilled,
      select: ({ findClientBySub }) => findClientBySub,
    }
  );

  useLayoutEffect(() => {
    if (isAdmin || isPending || !client) return;

    const { representativeFirstName, selectedMandataireId } = form.state.values.step0;
    if (representativeFirstName || selectedMandataireId) return;

    fillMandataireFormFromClient(form, client);
  }, [isAdmin, isPending, client, form]);

  return {
    /** False while the client profile is still loading for autofill. */
    isReady: isAdmin || alreadyFilled || (!isPending && (Boolean(client) || !sub)),
  } as const;
}
